import React from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowUp, faPen, faXmark } from "@fortawesome/free-solid-svg-icons"
import { motion } from "framer-motion"
import { DateTime } from "luxon"
import { theme } from "../settings/config"
import TextInput from "../inputs/TextInput"
import ToggleChip from "../atoms/ToggleChip"
import { getBillingRateToday } from "../utils/billing.utils"
import LessonTypeLabel from "../atoms/LessonTypeLabel"
import {
	isPrivateLessonType,
	lessonTypeHasPlayers,
} from "../utils/func.utils"
import { getOfferableLessonTypes, getProposedLessonTypes } from "../utils/school.utils"

// Quali lezioni si fanno qui, e a chi si fatturano. Fatturi tu in tutti e
// due i casi: quello che cambia è a chi, e quindi anche cosa vuol dire il
// numero accanto — quanto chiedi a ogni allievo, o quanto ti paga il
// circolo per un'ora.
//
// Un tipo sta in un gruppo solo, mai a metà: la freccia lo sposta. Spostare
// vale da lì in avanti, perché a chi è stata fatturata una lezione se lo
// scrive la lezione stessa quando la salvi.
const GROUPS = [
	{
		key: "players",
		title: "Da fatturare ai privati",
		hint: "Quanto chiedi a ogni allievo, per ora di lezione.",
		unit: "€/h a testa",
	},
	{
		key: "school",
		title: "Da fatturare al circolo",
		hint: "Quanto ti paga il circolo per un'ora, qualunque sia il numero di allievi.",
		unit: "€/h",
	},
]

// Quanti allievi ci stanno: è la differenza vera tra una privata e un CAD, e
// senza dirla i codici del listino si assomigliano tutti.
const playersLabel = lessonType => {
	const min = lessonType?.minimumPlayers
	const max = lessonType?.maxPlayers

	const allievi = n => (n === 1 ? "1 allievo" : `${n} allievi`)

	if (min && max) return min === max ? allievi(min) : `${min}-${max} allievi`
	if (min) return `da ${allievi(min)}`
	if (max) return `fino a ${allievi(max)}`
	return null
}

// Le tariffe al circolo vanno per data. Quelle scritte prima che le date
// esistessero sono un numero secco: valgono da sempre, e "da sempre" qui si
// scrive con una data che precede qualunque lezione.
const ALWAYS = "2000-01-01"

const toDatedRates = rate =>
	typeof rate === "number" ? { [ALWAYS]: rate } : { ...(rate ?? {}) }

// La riga in vigore a una certa data, cioè l'ultima che comincia prima.
const rowInForce = (table, isoDate) =>
	Object.keys(table)
		.filter(from => from <= isoDate)
		.sort()
		.pop()

function LessonTypesEditor({
	sportID,
	sport,
	prices,
	billingRates,
	availableLessonTypes,
	schoolLessonTypes,
	lessonTypes,
	getLessonType,
	canEditTypes,
	onChangePrices,
	onChangeTypes,
	onChangeBilling,
	onCreateType,
	onEditType,
}) {
	const sportPrices = prices[sportID] ?? {}
	// Quelli che si possono mettere in lista: quelli dello sport più le
	// tipologie proprie. Quelli attivi però possono contenere anche un tipo
	// che non si propone più — chi lo usa già se lo tiene, e lo vede,
	// altrimenti non troverebbe il prezzo di una lezione che continua a fare
	// — e quello tra gli offerti non c'è: si può togliere, non riprendere.
	const offerableTypes = getOfferableLessonTypes(sport, lessonTypes, schoolLessonTypes)
	const activeTypes =
		availableLessonTypes[sportID] ?? getProposedLessonTypes(sport, getLessonType)
	const inactiveTypes = offerableTypes.filter(lessonType => !activeTypes.includes(lessonType))
	const today = DateTime.now().toISODate()

	// Il gruppo non è un dato a parte: un tipo con una tariffa al circolo si
	// fattura al circolo, uno senza si fattura ai privati.
	const isSchoolBilled = lessonType => billingRates?.[lessonType] !== undefined

	const setPrice = (lessonType, raw) => {
		const next = { ...sportPrices }

		// casella svuotata: il prezzo non esiste, non vale zero
		if (raw === "") delete next[lessonType]
		else next[lessonType] = Number(raw)

		const nextPrices = { ...prices }
		if (Object.keys(next).length === 0) delete nextPrices[sportID]
		else nextPrices[sportID] = next

		onChangePrices(nextPrices)
	}

	// Cambiare la tariffa del circolo non riscrive quella di prima: ne apre
	// una nuova da oggi, così i mesi già fatturati restano come sono stati
	// fatturati. Se il numero torna quello che era, la riga di oggi non
	// serve più.
	const setRate = (lessonType, raw) => {
		const table = toDatedRates(billingRates[lessonType])
		const value = raw === "" ? 0 : Number(raw)

		const previousFrom = rowInForce(
			Object.fromEntries(Object.entries(table).filter(([from]) => from < today)),
			today,
		)
		const next = { ...table }

		if (previousFrom !== undefined && table[previousFrom] === value) delete next[today]
		else next[today] = value

		onChangeBilling({ ...billingRates, [lessonType]: next })
	}

	const moveToSchool = lessonType =>
		onChangeBilling({ ...billingRates, [lessonType]: { [today]: 0 } })

	const moveToPlayers = lessonType => {
		const next = { ...billingRates }
		delete next[lessonType]
		onChangeBilling(next)
	}

	const setActive = types => onChangeTypes({ ...availableLessonTypes, [sportID]: types })

	const removeType = lessonType => {
		setActive(activeTypes.filter(type => type !== lessonType))
		setPrice(lessonType, "")
		moveToPlayers(lessonType)
	}

	// L'ordine lo dà lo sport, ma un tipo che non si propone più non sta in
	// quell'ordine: filtrando solo su quello spariva dalla lista proprio
	// mentre se ne aggiungeva un altro. Quelli così restano, in fondo.
	const addType = lessonType =>
		setActive([
			...offerableTypes.filter(type => activeTypes.includes(type) || type === lessonType),
			...activeTypes.filter(type => !offerableTypes.includes(type)),
		])

	if (!sportID) {
		return (
			<Text textSize="body" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
				Aggiungi prima i campi del circolo: le lezioni seguono gli sport che ci si giocano.
			</Text>
		)
	}

	const numberBox = (value, unit, onChange) => (
		<Div w="7.5rem" style={{ flexShrink: 0 }}>
			<TextInput
				type="number"
				value={value}
				m="0"
				h="2.75rem"
				textAlign="right"
				placeholder="—"
				p={{ l: "0.75rem", r: "3.75rem" }}
				handleChange={onChange}
				suffix={
					<Div pos="absolute" top="0.6rem" right="0.6rem" bg="transparent">
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{unit}
						</Text>
					</Div>
				}
			/>
		</Div>
	)

	const arrowButton = (icon, onClick, label) => (
		<motion.div whileTap={{ scale: 0.9 }}>
			<Div
				d="flex"
				align="center"
				justify="center"
				w="2.2rem"
				h="2.2rem"
				rounded="12px"
				bg={theme.colors.gray_light}
				cursor="pointer"
				title={label}
				onClick={onClick}>
				<FontAwesomeIcon icon={icon} color={theme.colors.gunmetal} size="sm" />
			</Div>
		</motion.div>
	)

	const typeRow = (lessonType, group) => {
		const rates = toDatedRates(billingRates?.[lessonType])
		const from = group.key === "school" ? rowInForce(rates, today) : null
		const lessonTypeData = getLessonType(lessonType)

		// Quanti allievi ci stanno, e da quando vale la tariffa: una tariffa
		// cominciata in un giorno preciso lo dice, perché quelle di prima
		// valgono ancora per i mesi di prima.
		//
		// Di una lezione senza nomi non si dice niente: che il centro estivo
		// non abbia un elenco di presenti non è una cosa da annunciare, si
		// vede aprendola.
		// Una privata dice già nel nome quanti sono: ripeterlo sotto è rumore.
		const details = [
			lessonTypeHasPlayers(lessonTypeData) && !isPrivateLessonType(lessonType)
				? playersLabel(lessonTypeData)
				: null,
			from && from !== ALWAYS
				? `dal ${DateTime.fromISO(from).toFormat("dd/MM/yy")}`
				: null,
		].filter(Boolean)

		return (
			<Div
				key={lessonType}
				d="flex"
				align="center"
				justify="space-between"
				p={{ y: "0.4rem" }}
				border="bottom"
				borderColor={theme.colors.gray_light}
				style={{ gap: "0.5rem" }}>
				{/* Il nome apre la tipologia: un terzo pulsante accanto al
				    prezzo strozzerebbe la riga, e la riga è già stretta. */}
				<Div
					style={{ minWidth: 0 }}
					cursor={canEditTypes ? "pointer" : "default"}
					onClick={() => canEditTypes && onEditType(lessonType)}>
					<Div d="flex" align="center" style={{ gap: "0.4rem" }}>
						<Text textSize="body" textWeight="bold">
							<LessonTypeLabel lessonTypeID={lessonType} lessonType={lessonTypeData} />
						</Text>
						{canEditTypes && (
							<FontAwesomeIcon
								icon={faPen}
								color={theme.colors.text_light}
								size="2xs"
							/>
						)}
					</Div>
					{/* cosa vuol dire quel codice: "CAD" non lo spiega il nome,
					    e un listino di sigle si legge solo se le conosci già */}
					{lessonTypeData?.lessonTypeDescription && (
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{lessonTypeData.lessonTypeDescription}
						</Text>
					)}
					{details.length > 0 && (
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{details.join(" · ")}
						</Text>
					)}
				</Div>

				<Div d="flex" align="center" style={{ flexShrink: 0, gap: "0.35rem" }}>
					{group.key === "school"
						? numberBox(getBillingRateToday(billingRates, lessonType) ?? "", "€/h", e =>
								setRate(lessonType, e.target.value),
							)
						: numberBox(sportPrices[lessonType] ?? "", "€/h", e =>
								setPrice(lessonType, e.target.value),
							)}

					{group.key === "players"
						? arrowButton(faArrowDown, () => moveToSchool(lessonType), "Al circolo")
						: arrowButton(faArrowUp, () => moveToPlayers(lessonType), "Ai privati")}

					<motion.div whileTap={{ scale: 0.9 }}>
						<Div
							cursor="pointer"
							p="0.25rem 0.3rem"
							onClick={() => removeType(lessonType)}>
							<FontAwesomeIcon
								icon={faXmark}
								color={theme.colors.text_light}
								size="sm"
							/>
						</Div>
					</motion.div>
				</Div>
			</Div>
		)
	}

	return (
		<>
			{GROUPS.map(group => {
				const types = activeTypes.filter(lessonType =>
					group.key === "school" ? isSchoolBilled(lessonType) : !isSchoolBilled(lessonType),
				)

				return (
					<Div key={group.key} m={{ b: "1.25rem" }}>
						<Text textSize="body" textWeight="bold" textColor={theme.colors.text_light}>
							{group.title}
						</Text>
						<Text
							textSize="caption"
							textColor={theme.colors.text_light}
							m={{ b: "0.5rem" }}>
							{group.hint}
						</Text>

						{types.length === 0 ? (
							<Text textSize="caption" textColor={theme.colors.text_light}>
								Nessuna, per ora.
							</Text>
						) : (
							types.map(lessonType => typeRow(lessonType, group))
						)}
					</Div>
				)
			})}

			{inactiveTypes.length > 0 && (
				<Div m={{ t: "1rem" }}>
					<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.5rem" }}>
						Qui non li fai
					</Text>
					<Div d="flex" flexWrap="wrap" style={{ gap: "0.5rem" }}>
						{inactiveTypes.map(lessonType => (
							<ToggleChip
								key={lessonType}
								text={
									<LessonTypeLabel
										lessonTypeID={lessonType}
										lessonType={getLessonType(lessonType)}
									/>
								}
								isSelected={false}
								onClick={() => addType(lessonType)}
							/>
						))}
					</Div>
				</Div>
			)}

			{/* Il catalogo copre la scuola tennis classica e non tutto il
			    resto: il live ball, il centro estivo, la cosa che in quel
			    circolo chiamano solo loro in quel modo. Quelle se le scrive
			    il circolo, e le scrive chi lo gestisce: non sono tipologie
			    di un maestro, sono quello che lì dentro si fa. */}
			{canEditTypes && (
				<Div m={{ t: "1.25rem" }}>
					<Text
						textSize="caption"
						textColor={theme.colors.text_light}
						m={{ b: "0.5rem" }}>
						Non c'è quella che fate?
					</Text>
					<ToggleChip
						text="Aggiungi una tipologia del circolo"
						isSelected={false}
						onClick={onCreateType}
					/>
				</Div>
			)}

			<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "1rem", b: "1rem" }}>
				La freccia sposta un tipo nell'altro gruppo, e vale da qui in avanti: le lezioni
				già fatte restano fatturate a chi le hai fatturate.
			</Text>
		</>
	)
}

export default LessonTypesEditor
